import Typography from "components/basic/typography";
import { useTranslation } from "react-i18next";
import HomeChargerImage from "resources/images/HomeChargerImage.png";
import { useNavigate } from "react-router-dom";
import SignUpFormLayout from "../../components/SignUpFormLayout";
import TextField from "components/basic/textField";
import { useSnack } from "hooks/useSnack";
import { useFieldErrors } from "hooks/useFieldErrors";
import { useMaskedInput } from "hooks/useMaskedInput";
import UploadFile from "components/basic/uploadFile";
import { Vendor } from "../..";
import DatePicker from "components/basic/datePicker";
import {
  BUSINESS_NUMBER_PLACEHOLDER,
  formatBusinessNumber,
  todayIsoDate,
} from "pages/formTools";
import { validateBusinessRegistration } from "./validation";

interface BusinessRegistrationFormProps {
  signUpData: Vendor;
  setSignUpData: React.Dispatch<React.SetStateAction<Vendor>>;
}

const BusinessRegistrationForm: React.FC<BusinessRegistrationFormProps> = ({
  signUpData,
  setSignUpData,
}) => {
  const navigate = useNavigate();
  const showSnack = useSnack();
  const { t } = useTranslation("auth");

  const { errors, validateAll, revalidate } = useFieldErrors(
    validateBusinessRegistration,
  );
  const handleBusinessNumberChange = useMaskedInput(formatBusinessNumber);

  // Applies an edit and re-checks any error already on screen.
  const updateField = (patch: Partial<Vendor>) => {
    const next = { ...signUpData, ...patch };
    setSignUpData(next);
    revalidate(next);
  };

  const handleNext = () => {
    if (!validateAll(signUpData)) {
      showSnack(t("common.fix_errors"), "error");
      return;
    }

    navigate("?currentPage=signatureForm");
  };

  return (
    <SignUpFormLayout
      imageSrc={HomeChargerImage}
      backAction={{
        label: t("common.back"),
        color: "primary",
        onClick: () => navigate("?currentPage=privacyContactForm"),
      }}
      primaryAction={{
        label: t("common.next"),
        color: "warning",
        onClick: handleNext,
      }}
    >
      <div>
        <Typography variant="body2" align="inherit">
          {t("registration.business_number")}
        </Typography>

        <TextField
          placeHolder={BUSINESS_NUMBER_PLACEHOLDER}
          name="businessNumber"
          value={signUpData.businessNumber ?? ""}
          size="tiny"
          textAlign="right"
          onChange={(event) =>
            updateField({
              businessNumber: handleBusinessNumberChange(
                event,
                signUpData.businessNumber ?? "",
              ),
            })
          }
          error={errors.businessNumber ?? undefined}
        />
      </div>

      <div>
        <Typography variant="body2" align="inherit">
          {t("registration.proof_registration")}
        </Typography>

        <UploadFile
          label={t("common.upload")}
          onChange={(files) => {
            updateField({ businessLicense: files });
          }}
          file={signUpData.businessLicense}
          error={errors.businessLicense ?? undefined}
        />
      </div>

      <div>
        <Typography variant="body2" align="inherit">
          {t("registration.proof_insurance")}
        </Typography>

        <UploadFile
          label={t("common.upload")}
          onChange={(files) => {
            updateField({ proofOfInsurance: files });
          }}
          file={signUpData.proofOfInsurance}
          error={errors.proofOfInsurance ?? undefined}
        />
      </div>

      <div>
        <Typography variant="body2" align="inherit">
          {t("registration.insurance_expiry")}
        </Typography>

        <DatePicker
          value={signUpData.insuranceExpiryDate}
          minDate={todayIsoDate()}
          onChange={(insuranceExpiryDate) =>
            updateField({ insuranceExpiryDate })
          }
          error={errors.insuranceExpiryDate ?? undefined}
        />
      </div>
    </SignUpFormLayout>
  );
};

export default BusinessRegistrationForm;
